import { z } from 'zod'
import { HugeiconsIcon } from '@hugeicons/react'
import { UserCheck01Icon } from '@hugeicons/core-free-icons'

import { useConvexUpdateUserRoles } from '../services/users.convex'
import {
  useConvexOrganizationRoles,
  useUserRolesInOrg,
} from '@/features/organization/services/organization.convex'
import { useMyConvexOrganizationId } from '@/features/auth'
import type { AnyFieldApi } from '@tanstack/react-form'
import { FieldInfo } from '@/components/common/field-info'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useFormDialog } from '@/contexts/form-dialog-context'

interface EditUserRolesButtonProps {
  userId: string
  userName: string
  children?: React.ReactNode
  onSuccess?: () => void
}

interface EditUserRolesFormData {
  roleIds: Array<string>
}

const EDIT_USER_ROLES_SCHEMA = z.object({
  roleIds: z.array(z.string()),
})

const MEMBER_ROLE_KEY = 'member'

export const EditUserRolesButton = ({
  userId,
  userName,
  children,
  onSuccess,
}: EditUserRolesButtonProps) => {
  const { showFormDialog } = useFormDialog()
  const convexOrgId = useMyConvexOrganizationId()
  const updateUserRolesMutation = useConvexUpdateUserRoles()

  // Fetch available organization roles and current user roles
  const { data: organizationRoles, isLoading: isOrgRolesLoading } =
    useConvexOrganizationRoles()
  const { data: userRoles, isLoading: isUserRolesLoading } = useUserRolesInOrg(
    userId,
    convexOrgId,
  )

  const handleSubmit = async (data: EditUserRolesFormData) => {
    const currentRoleIds = userRoles?.map((role) => role.id) || []
    const rolesChanged =
      data.roleIds.length !== currentRoleIds.length ||
      !data.roleIds.every((id) => currentRoleIds.includes(id))

    if (!rolesChanged) return

    await updateUserRolesMutation.mutateAsync({
      userId,
      roleIds: data.roleIds,
    })
  }

  const handleEditRolesClick = () => {
    if (isOrgRolesLoading || isUserRolesLoading) {
      return
    }

    const currentRoleIds = userRoles?.map((role) => role.id) || []

    showFormDialog<EditUserRolesFormData, void>({
      title: `Edit Roles for ${userName}`,
      description: 'Select the roles to assign to this user',
      submitText: 'Update Roles',
      showSuccessState: false,
      formOptions: {
        defaultValues: {
          roleIds: currentRoleIds,
        },
      },
      children: (form) => {
        return (
          <>
            {/* Roles Selection */}
            <form.Field
              name="roleIds"
              validators={{
                onChange: EDIT_USER_ROLES_SCHEMA.shape.roleIds,
              }}
            >
              {(field: AnyFieldApi) => (
                <div className="space-y-4">
                  <Label className="text-sm font-medium">User Roles</Label>
                  <div className="space-y-3">
                    {organizationRoles?.map((role) => {
                      const isChecked =
                        field.state.value?.includes(role.id) || false
                      const isMemberRole = role.key === MEMBER_ROLE_KEY

                      return (
                        <div
                          key={role.id}
                          className="flex items-start space-x-3"
                        >
                          <Checkbox
                            id={role.id}
                            checked={isChecked}
                            disabled={isMemberRole}
                            onCheckedChange={(checked) => {
                              const currentValue = field.state.value || []
                              if (checked) {
                                field.handleChange([...currentValue, role.id])
                              } else {
                                field.handleChange(
                                  currentValue.filter(
                                    (id: string) => id !== role.id,
                                  ),
                                )
                              }
                            }}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <Label
                              htmlFor={role.id}
                              className={`text-sm font-medium cursor-pointer ${
                                isMemberRole ? 'text-muted-foreground' : ''
                              }`}
                            >
                              {role.name.charAt(0).toUpperCase() +
                                role.name.slice(1)}
                            </Label>
                            <p className="text-xs text-muted-foreground mt-1">
                              {role.description}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <FieldInfo field={field} />
                </div>
              )}
            </form.Field>
          </>
        )
      },
      onSubmit: handleSubmit,
      onSuccess,
    })
  }

  return (
    <Button
      onClick={handleEditRolesClick}
      variant="outline"
      size="sm"
      disabled={isOrgRolesLoading || isUserRolesLoading}
    >
      {children || (
        <>
          <HugeiconsIcon icon={UserCheck01Icon} className="h-4 w-4 mr-2" />
          Edit Roles
        </>
      )}
    </Button>
  )
}
